import {Typography} from '@mui/material';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurButton from '../../common/aur-button/AurButton';
import {t} from 'i18next';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {useState} from 'react';
import {TreeViewBaseItem} from '@mui/x-tree-view/models';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {extractSavedSearchId, isValidName} from '../../../utils/utils';
import EcaAddToSavedSearchesTemplateDrawerPanel from './EcaAddToSavedSearchesTemplateDrawerPanel';
import {AxiosError} from 'axios';
import {addSavedSearchToTemplate} from '../../../api/savedSearchTemplateService';
import {useToast} from '../../../contexts/toastContext/ToastContext';

type ExtendedTreeItemProps = {
  fileType: string;
  id: string;
  label: string;
  count?: string;
  fieldId?: string;
  newFolder?: boolean;
  createdBy?: string;
  createdDate?: string;
  updatedDate?: string;
  updatedBy?: string;
  parentFolder?: string;
  numberOfChild?: number;
  name?: string;
  value?: string;
  exportId?: number;
  type?: string;
  hideAddFolder?: boolean;
  isScopeSelected?: boolean;
  parentGroupId?: string;
  directory_structure_ids?: string;
  parentTagName?: string;
  displayOrder?: number;
  isDefault?: boolean;
  ecaSearchId?: string;
};

export interface EcaSaveSearchProps {
  setShowDrawer: (value: boolean) => void;
  handleCloseDrawer: () => void;
  onSaveToTemplate?: (value: boolean) => void;
  showDrawer: boolean;
  searchClientId: string;
  searchId: string;
  searchName?: string;
}

interface AddSavedSearchToTemplateData {
  name: string;
  savedSearchId: string;
  groupId: string | undefined;
}

const EcaAddToSaveSearchTemplate = (props: EcaSaveSearchProps) => {
  const {
    setShowDrawer,
    handleCloseDrawer,
    onSaveToTemplate,
    showDrawer,
    searchClientId,
    searchId,
    searchName = '',
  } = props;
  const [apiLoading] = useState<boolean>(false);
  const [renameSearchError, setRenameSearchError] = useState<boolean>(false);
  const [name, setName] = useState<string>(searchName);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(
    ''
  );
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] =
    useState<boolean>(false);

  const {showToast} = useToast();

  const selectedIndex = 'Standard Index';
  const clientId = searchClientId;

  const handleOnSelectedTreeItem = (
    selectedItem: TreeViewBaseItem<ExtendedTreeItemProps> | null
  ) => {
    if (selectedItem?.fileType === 'folder') {
      setSelectedGroupId(selectedItem?.id);
      setIsSaveButtonEnabled(true);
    } else {
      setSelectedGroupId('');
      setIsSaveButtonEnabled(false);
    }
  };

  const handleCancelDrawer = () => {
    setShowDrawer(false);
    handleCloseDrawer();

    setSelectedGroupId('');
    setIsSaveButtonEnabled(false);
  };

  const handleSaveClick = async () => {
    setIsSaveButtonEnabled(false);

    const requestBody: AddSavedSearchToTemplateData = {
      name: name,
      savedSearchId: extractSavedSearchId(searchId),
      groupId: extractSavedSearchId(selectedGroupId),
    };
    try {
      const results = await addSavedSearchToTemplate(clientId, requestBody);

      if (results) {
        handleCancelDrawer();
        onSaveToTemplate?.(true);
        showToast(
          t('eca.flyout.addToTemplate.saveSuccessMessage'),
          'success',
          undefined,
          'center'
        );
      }
    } catch (error: unknown) {
      const e = error as AxiosError;
      showToast(e.message, 'error', undefined, 'center');
    } finally {
      setIsSaveButtonEnabled(true);
    }
  };

  const contentBody = apiLoading ? (
    <AurLoadingIndicator show={true} size={48} className="loader-save-search" />
  ) : (
    <div
      className="add-to-saved-search-template-container"
      data-testid="add-to-saved-search-template-panel"
    >
      <div className="save-search-container">
        <div className="panel-header">
          <div>
            <AurButton
              classNames={'drawer-close-button panel-close'}
              label={t('buttons.cancel')}
              type="exit"
              size="Medium"
              title={t('buttons.cancel')}
              dataTestId="cancel-btn"
              onClick={handleCancelDrawer}
            />
          </div>
        </div>
        <div className="panel-title">
          <div className="panel-header-container">
            <div className="panel-header-title">
              <Typography className="typo-text headline1_size custom-header">
                {t('eca.flyout.addToTemplate.addToTemplateTitle')}
              </Typography>
            </div>
          </div>
          <div className="panel-input-container">
            <AurTextField
              error={renameSearchError}
              helperText={
                renameSearchError ? t('eca.flyout.addToTemplate.nameError') : ''
              }
              onChange={(e) => {
                const newQuery = e.target.value;
                setRenameSearchError(!isValidName(newQuery));
                setName(newQuery);
              }}
              variant="filled"
              className={`aur-textfield`}
              label={t('eca.flyout.addToTemplate.nameSearch')}
              placeholder={t('eca.flyout.addToTemplate.nameSearchText')}
              ariaLabel={t('eca.flyout.addToTemplate.nameSearch')}
              value={name}
              inputProps={{
                maxLength: 50,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
        </div>

        <div className={`location-container`}>
          <div className="location-header">
            <div>
              <Typography className="typo-text label2_size custom-text-style-location">
                {t('eca.flyout.addToTemplate.location')}
              </Typography>
            </div>
            <div>
              <Typography className="typo-text body3_size custom-text-style">
                {t('eca.flyout.addToTemplate.contactAdmin')}
              </Typography>
            </div>
          </div>
          <div className="location-body">
            <div className="location-content">
              <div className="location-treeview-container">
                <div className="location-treeview-content">
                  <EcaAddToSavedSearchesTemplateDrawerPanel
                    selectedIndex={selectedIndex}
                    enableFlayout={false}
                    openForPickSavedSearch={false}
                    enableSearch={false}
                    folderDetailsView={true}
                    showActions={true}
                    manageSaved={true}
                    manageSearchRename={true}
                    clientId={clientId}
                    handleOnSelectedTreeItem={handleOnSelectedTreeItem}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="panel-footer">
          <div className="save-btn-conatiner">
            <div>
              <AurButton
                classNames={'drawer-close-button panel-close'}
                label={t('buttons.cancel')}
                type="exit"
                size="Medium"
                title={t('buttons.cancel')}
                dataTestId="cancel-btn"
                onClick={handleCancelDrawer}
              />
            </div>
            <AurButton
              classNames={'drawer-close-button panel-close save-btn'}
              label={t('buttons.save')}
              type="continue"
              size="Medium"
              title={t('buttons.save')}
              onClick={handleSaveClick}
              disabled={
                !name.trim() || renameSearchError || !isSaveButtonEnabled
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <AurDrawer
      className="saved-search-drawer"
      open={showDrawer}
      onClose={handleCancelDrawer}
      anchor="right"
      drawerContent={contentBody}
    />
  );
};

export default EcaAddToSaveSearchTemplate;
